import React from 'react';
import { useSelector } from 'react-redux';
import { useGetCurrentTenantQuery } from '../api/tenant';
import { useGetUserQuery } from '../api/user';
import { RootState } from '../Store/store';
import ContentCard from '../Layouts/ContentCard';
import LoadingSpiner from '../Components/Loader/LoadingSpiner';
import CreditMeter from '../Components/Subscription/CreditMeter';
import { 
  getTierDisplayName, 
  getTierDescription, 
  getTierFeatures, 
  getAvailableFeatures,
  getFeatureLimit,
  isFeatureUnlimited,
  isFeatureAvailable,
  FEATURE_KEYS
} from '../constants/subscriptionTiers';
import { useCanUseAPI, useCanManageTeam, useCanManageAccount } from '../hooks/usePermissions';
import { useCreatePaymentPortalSessionMutation } from '../api/checkout';

export default function BillingPage() {
    const { data: tenantData, isLoading: tenantLoading, error: tenantError } = useGetCurrentTenantQuery(undefined);
    const { data: userData, isLoading: userLoading, error: userError } = useGetUserQuery(undefined);
    const authState = useSelector((state: RootState) => state.auth);
    const { canManageTeam } = useCanManageTeam();
    const userRole = userData?.role;
    const { canUseAPI } = useCanUseAPI();
    const { canManageAccount } = useCanManageAccount();
    const [createPaymentPortalSession, { isLoading: isPortalLoading }] = useCreatePaymentPortalSessionMutation();
    
    const isLoading = tenantLoading || userLoading;
    const error = tenantError || userError;

    const handleManageSubscription = async () => {
        try {
            const result = await createPaymentPortalSession().unwrap();
            
            if (result && result.portal_url) {
                console.log('Redirecting to payment portal:', result.portal_url);
                // Redirect to the Stripe customer portal URL
                window.location.href = result.portal_url;
            } else {
                console.error('Failed to get payment portal URL');
            }
        } catch (error) {
            console.error('Payment portal error:', error);
        }
    };

    if (isLoading) {
        return (
            <div className="container mx-auto">
                <ContentCard title="Loading Billing Information">
                    <div className="flex justify-center items-center py-8">
                        <LoadingSpiner />
                        <span className="ml-2">Loading billing information...</span>
                    </div>
                </ContentCard>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto">
                <ContentCard title="Billing & Account Management">
                    <div className="alert alert-error">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <h3 className="font-bold">Unable to Load Billing Information</h3>
                            <div className="text-xs">There was an error loading your billing information. Please try again later.</div>
                        </div>
                    </div>
                </ContentCard>
            </div>
        );
    }

    return (
        <div className="container mx-auto">
            <ContentCard title="Billing & Account Management">
                <div className="mb-6">
                    <p className="text-base-content/70">
                        Manage your subscription, billing information, and account settings.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Plan */}
                <ContentCard title="Current Plan">
                    <div className="space-y-4">
                        <div className="text-xl font-semibold text-base-content">
                            {getTierDisplayName(tenantData?.subscription || 'free')}
                        </div>
                        <div className="text-sm text-base-content/70">
                            {getTierDescription(tenantData?.subscription || 'free')}
                        </div>
                        <div className="text-sm text-base-content/70">
                            Status: <span className={`font-medium ${tenantData?.status === 'Active' ? 'text-success' : 'text-warning'}`}>
                                {tenantData?.status || 'Unknown'}
                            </span>
                        </div>
                        <div className="space-y-2">
                            {/* Team Collaboration */}
                            <div className="flex items-center text-sm text-base-content/70">
                                <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Team Collaboration: {getFeatureLimit(tenantData?.subscription || 'free', FEATURE_KEYS.TEAM_COLLABORATION) || 'Not available'}
                            </div>
                            {/* Image Upload */}
                            <div className="flex items-center text-sm text-base-content/70">
                                <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Image Upload: {getFeatureLimit(tenantData?.subscription || 'free', FEATURE_KEYS.IMAGE_UPLOAD) || 'Not available'}
                            </div>
                            {/* Image Analysis */}
                            <div className="flex items-center text-sm text-base-content/70">
                                <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Image Analysis: {getFeatureLimit(tenantData?.subscription || 'free', FEATURE_KEYS.IMAGE_ANALYSIS) || 'Not available'}
                            </div>
                            {/* Project Creation */}
                            <div className="flex items-center text-sm text-base-content/70">
                                <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Project Creation: {getFeatureLimit(tenantData?.subscription || 'free', FEATURE_KEYS.PROJECT_CREATION) || 'Not available'}
                            </div>
                            {/* API Access */}
                            <div className="flex items-center text-sm text-base-content/70">
                                <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                API Access: {getFeatureLimit(tenantData?.subscription || 'free', FEATURE_KEYS.API_ACCESS) || 'Not available'}
                            </div>
                            {/* Your Role */}
                            <div className="flex items-center text-sm text-base-content/70">
                                <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                Your Role: {userData?.role || 'Unknown'}
                            </div>
                        </div>
                    </div>
                </ContentCard>

                {/* Usage Statistics */}
                <ContentCard title="Usage This Month">
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm text-base-content/70 mb-1">
                                <span>Your Storage Used</span>
                                <span>{(() => {
                                    if (!userData?.storageUsage?.usage || !userData?.storageUsage?.quota) return '0 GB';
                                    
                                    const usageGB = (parseInt(userData.storageUsage.usage) / (1024 * 1024 * 1024)).toFixed(2);
                                    const quotaGB = (parseInt(userData.storageUsage.quota) / (1024 * 1024 * 1024)).toFixed(2);
                                    const isUnlimited = parseInt(userData.storageUsage.quota) > (1024 * 1024 * 1024 * 1024 * 10000); // 10,000 TB in bytes
                                    
                                    return isUnlimited ? `${usageGB} / Unlimited GB` : `${usageGB} / ${quotaGB} GB`;
                                })()}</span>
                            </div>
                            <progress
                                className="progress progress-primary w-full h-2"
                                value={userData?.storageUsage?.usage && userData?.storageUsage?.quota ?
                                    Math.min((parseInt(userData.storageUsage.usage) / parseInt(userData.storageUsage.quota)) * 100, 100) : 0}
                                max={100}
                            ></progress>
                        </div>

                        <div>
                            <div className="flex justify-between text-sm text-base-content/70 mb-1">
                                <span>Upload Credits Remaining (Shared/Month)</span>
                                <span>{(() => {
                                    const uploadCredits = tenantData?.credits?.available?.upload || 0;
                                    const uploadBatches = tenantData?.credits?.upload || [];
                                    const uploadUnlimited = uploadBatches.some((batch: { initial_amount?: number }) => (batch.initial_amount || 0) > 999999);
                                    const uploadTotal = uploadBatches.reduce((sum: number, batch: { initial_amount?: number }) => sum + (batch.initial_amount || 0), 0);
                                    return uploadUnlimited ? 'Unlimited' : `${uploadCredits.toLocaleString()} / ${uploadTotal.toLocaleString()}`;
                                })()}</span>
                            </div>
                            <CreditMeter
                                batches={tenantData?.credits?.upload || []}
                                unlimited={(tenantData?.credits?.upload || []).some((batch: { initial_amount?: number }) => (batch.initial_amount || 0) > 999999)}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between text-sm text-base-content/70 mb-1">
                                <span>Analyze Credits Remaining (Shared/Month)</span>
                                <span>{(() => {
                                    const analyzeCredits = tenantData?.credits?.available?.analyze || 0;
                                    const analyzeBatches = tenantData?.credits?.analyze || [];
                                    const analyzeUnlimited = analyzeBatches.some((batch: { initial_amount?: number }) => (batch.initial_amount || 0) > 999999);
                                    const analyzeTotal = analyzeBatches.reduce((sum: number, batch: { initial_amount?: number }) => sum + (batch.initial_amount || 0), 0);
                                    return analyzeUnlimited ? 'Unlimited' : `${analyzeCredits.toLocaleString()} / ${analyzeTotal.toLocaleString()}`;
                                })()}</span>
                            </div>
                            <CreditMeter
                                batches={tenantData?.credits?.analyze || []}
                                unlimited={(tenantData?.credits?.analyze || []).some((batch: { initial_amount?: number }) => (batch.initial_amount || 0) > 999999)}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between text-sm text-base-content/70 mb-1">
                                <span>Team Seats Used</span>
                                <span>{tenantData?.seatAllocation?.usage || 0} / {tenantData?.seatAllocation?.quota || 0}</span>
                            </div>
                            <progress
                                className="progress progress-primary w-full h-2"
                                value={tenantData?.seatAllocation?.usage && tenantData?.seatAllocation?.quota ?
                                    Math.min((tenantData.seatAllocation.usage / tenantData.seatAllocation.quota) * 100, 100) : 0}
                                max={100}
                            ></progress>
                        </div>
                    </div>
                </ContentCard>


                {/* Account Actions */}
                <ContentCard title="Account Actions">
                    <div className="space-y-3">
                        {canManageAccount && (
                            <button 
                                className="btn btn-primary w-full justify-start"
                                onClick={handleManageSubscription}
                                disabled={isPortalLoading}
                            >
                                {isPortalLoading ? (
                                    <div className="flex items-center whitespace-nowrap">
                                        <span className="loading loading-spinner loading-sm"></span>
                                        <span className="ml-2">Loading...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center whitespace-nowrap">
                                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                        <span className="ml-2">Manage Subscription</span>
                                    </div>
                                )}
                            </button>
                        )}
                        
                        {canManageAccount && canManageTeam(userRole) && (
                            <button className="btn btn-outline w-full justify-start">
                                <div className="flex items-center whitespace-nowrap">
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <span className="ml-2">Team Management</span>
                                </div>
                            </button>
                        )}
                        
                        {canUseAPI && (
                            <button className="btn btn-outline w-full justify-start">
                                <div className="flex items-center whitespace-nowrap">
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="ml-2">API Keys</span>
                                </div>
                            </button>
                        )}
                        
                    </div>
                </ContentCard>
                </div>
            </ContentCard>
        </div>
    );
}
